import React, { useEffect, useState } from 'react';
import { Plus, BookOpen, Users, Eye, Settings } from 'lucide-react';
import { Layout } from '../components/Layout/Layout';
import { ResourceCard } from '../components/Resources/ResourceCard';
import { ResourceViewer } from '../components/Resources/ResourceViewer';
import { ResourceFilters } from '../components/Resources/ResourceFilters';
import { VisibilityManager } from '../components/Resources/VisibilityManager';
import { ResourceCreateModal } from '../components/Resources/ResourceCreateModal';
import { useResourceStore } from '../store/resourceStore';
import { useAuthStore } from '../store/authStore';
import type { Resource, ResourceFilters as ResourceFiltersType } from '../types/resources';
import { ProfileType } from '../types';

export const ResourcesPage: React.FC = () => {
  const { user } = useAuthStore();
  const { 
    resources, 
    userResources, 
    categories, 
    currentResource,
    isLoading, 
    fetchResources, 
    fetchUserResources,
    fetchCategories,
    toggleLike 
  } = useResourceStore();

  const [filters, setFilters] = useState<ResourceFiltersType>({});
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [showVisibilityManager, setShowVisibilityManager] = useState(false);
  const [resourceForVisibility, setResourceForVisibility] = useState<Resource | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewMode, setViewMode] = useState<'all' | 'assigned'>('assigned');

  useEffect(() => {
    fetchCategories();
    if (user?.profile_type === ProfileType.PORTEUR) {
      fetchUserResources();
      setViewMode('assigned');
    } else {
      fetchResources(filters);
      setViewMode('all');
    }
  }, [user?.profile_type]);

  useEffect(() => {
    if (viewMode === 'all') {
      fetchResources(filters);
    }
  }, [filters, viewMode]);

  const handleResourceView = (resource: Resource) => {
    setSelectedResource(resource);
  };

  const handleManageVisibility = (resource: Resource) => {
    setResourceForVisibility(resource);
    setShowVisibilityManager(true);
  };

  const handleVisibilitySuccess = () => {
    setShowVisibilityManager(false);
    setResourceForVisibility(null);
    // Refresh data
    if (viewMode === 'all') {
      fetchResources(filters);
    } else {
      fetchUserResources();
    }
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    fetchResources(filters);
  };

  const canManageResources = user && [ProfileType.COORDINATEUR, ProfileType.ADMIN].includes(user.profile_type as ProfileType);
  const canCreateResources = user && [ProfileType.COORDINATEUR, ProfileType.ADMIN, ProfileType.ACCOMPAGNATEUR].includes(user.profile_type as ProfileType);

  const displayResources = viewMode === 'assigned' ? userResources : resources;
  const featuredResources = displayResources.filter(r => r.is_featured);

  // Group resources by category
  const resourcesByCategory = categories.map(category => ({
    ...category,
    resources: displayResources.filter(r => r.category === category.slug)
  })).filter(cat => cat.resources.length > 0);

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Ressources Pédagogiques</h1>
            <p className="text-gray-600 mt-1">
              {user?.profile_type === ProfileType.PORTEUR 
                ? 'Ressources sélectionnées pour vous accompagner dans votre projet ESS'
                : 'Bibliothèque de ressources pour l\'accompagnement des porteurs de projet'
              }
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            {canManageResources && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('assigned')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    viewMode === 'assigned' 
                      ? 'bg-blue-600 text-white' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Mes ressources
                </button>
                <button
                  onClick={() => setViewMode('all')}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    viewMode === 'all' 
                      ? 'bg-blue-600 text-white' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  Toutes les ressources
                </button>
              </div>
            )}
            
            {canCreateResources && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Nouvelle ressource</span>
              </button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total ressources</p>
                <p className="text-2xl font-bold text-gray-900">{displayResources.length}</p>
              </div>
              <BookOpen className="w-8 h-8 text-blue-400" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">À la une</p>
                <p className="text-2xl font-bold text-yellow-600">{featuredResources.length}</p>
              </div>
              <Eye className="w-8 h-8 text-yellow-400" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Thématiques</p>
                <p className="text-2xl font-bold text-purple-600">{resourcesByCategory.length}</p>
              </div>
              <Settings className="w-8 h-8 text-purple-400" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Vues totales</p>
                <p className="text-2xl font-bold text-green-600">
                  {displayResources.reduce((sum, r) => sum + r.view_count, 0)}
                </p>
              </div>
              <Users className="w-8 h-8 text-green-400" />
            </div>
          </div>
        </div>

        {/* Filters (only for coordinators/admins viewing all resources) */}
        {viewMode === 'all' && (
          <ResourceFilters
            filters={filters}
            onFiltersChange={setFilters}
            categories={categories}
            resultCount={displayResources.length}
          />
        )}

        {/* Featured Resources */}
        {featuredResources.length > 0 && (
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <h2 className="text-xl font-semibold text-gray-900">⭐ Ressources à la une</h2>
              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-sm rounded-full">
                {featuredResources.length}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredResources.map((resource) => (
                <div key={resource.id} className="relative">
                  <ResourceCard
                    resource={resource}
                    onView={handleResourceView}
                    onLike={toggleLike}
                    showVisibilityBadge={user?.profile_type === ProfileType.PORTEUR}
                  />
                  {canManageResources && (
                    <button
                      onClick={() => handleManageVisibility(resource)}
                      className="absolute top-4 right-4 p-2 bg-white/90 hover:bg-white rounded-lg shadow-md transition-colors"
                      title="Gérer la visibilité"
                    >
                      <Eye className="w-4 h-4 text-blue-600" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Resources by Category */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : resourcesByCategory.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {user?.profile_type === ProfileType.PORTEUR 
                ? 'Aucune ressource assignée'
                : 'Aucune ressource trouvée'
              }
            </h3>
            <p className="text-gray-500 mb-6">
              {user?.profile_type === ProfileType.PORTEUR 
                ? 'Votre accompagnateur vous donnera accès aux ressources pertinentes pour votre projet.'
                : 'Commencez par créer des ressources pour accompagner les porteurs de projet.'
              }
            </p>
            {canCreateResources && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Créer la première ressource
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-8">
            {resourcesByCategory.map((category) => (
              <div key={category.id}>
                <div className="flex items-center space-x-3 mb-4">
                  <span className="text-2xl">{category.icon}</span>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">{category.name}</h2>
                    <p className="text-sm text-gray-600">{category.description}</p>
                  </div>
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                    {category.resources.length}
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {category.resources.map((resource) => (
                    <div key={resource.id} className="relative">
                      <ResourceCard
                        resource={resource}
                        onView={handleResourceView}
                        onLike={toggleLike}
                        showVisibilityBadge={user?.profile_type === ProfileType.PORTEUR}
                      />
                      {canManageResources && (
                        <button
                          onClick={() => handleManageVisibility(resource)}
                          className="absolute top-4 right-4 p-2 bg-white/90 hover:bg-white rounded-lg shadow-md transition-colors"
                          title="Gérer la visibilité"
                        >
                          <Eye className="w-4 h-4 text-blue-600" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Resource Viewer Modal */}
        {selectedResource && (
          <ResourceViewer
            resource={selectedResource}
            onClose={() => setSelectedResource(null)}
            onLike={toggleLike}
          />
        )}

        {/* Visibility Manager Modal */}
        {showVisibilityManager && resourceForVisibility && (
          <VisibilityManager
            resource={resourceForVisibility}
            onClose={() => {
              setShowVisibilityManager(false);
              setResourceForVisibility(null);
            }}
            onSuccess={handleVisibilitySuccess}
          />
        )}

        {/* Create Resource Modal */}
        <ResourceCreateModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      </div>
    </Layout>
  );
};